import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Target, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

const Card = ({ label, value, sub, testid }) => (
  <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4" data-testid={testid}>
    <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">{label}</div>
    <div className="font-display font-black text-2xl mt-1">{value}</div>
    {sub && <div className="text-[11px] font-mono-c text-white/40 mt-0.5">{sub}</div>}
  </div>
);

const Bar = ({ label, count, pctv }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="w-24 sm:w-32 text-white/65 truncate" title={label}>{label}</span>
    <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full bg-[#00FF88]/70 rounded-full" style={{ width: `${Math.min(100, pctv)}%` }} />
    </div>
    <span className="font-mono-c text-[11px] text-white/60 w-16 text-right">{count} · {pct(pctv)}</span>
  </div>
);

const Section = ({ icon: Icon, eyebrow, title, children }) => (
  <section className="glass rounded-2xl p-5 mb-5">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-[#00FF88]" />
      <span className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88]">{eyebrow}</span>
    </div>
    <h3 className="font-display font-extrabold text-xl mb-4">{title}</h3>
    {children}
  </section>
);

const PilotMetrics = ({ token, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`${API}/pilot/analytics`, { headers: { "X-Admin-Token": token } });
        if (!cancelled) { setData(res.data); setLoading(false); }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    run();
    return () => { cancelled = true; };
  }, [token, refreshKey]);

  if (loading) return <div className="py-10 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!data || data.total === 0) {
    return (
      <div className="glass rounded-2xl p-6 text-center text-white/45 text-sm">
        Metrics will appear here once applications start coming in.
      </div>
    );
  }

  const v = data.validation || {};
  const inv = data.investor || {};
  const d = data.demand || {};

  return (
    <div data-testid="pilot-metrics">
      {/* Investor snapshot — the one to screenshot for decks */}
      <Section icon={TrendingUp} eyebrow="// Investor snapshot" title="Headline metrics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="Total demand"          value={inv.total_demand?.toLocaleString() || "0"} sub={`${data.statuses?.New || 0} new`} testid="m-demand" />
          <Card label="Total funded"          value={fmtMoney(inv.total_funded)} sub={`${data.statuses?.Funded || 0} funded`} testid="m-funded" />
          <Card label="Repayment rate"        value={pct(inv.repayment_rate)} sub={`${pct(inv.average_repayment_time_days ? inv.average_repayment_time_days * 1 : 0)} avg days`.replace("0%","—").replace(/^0\.0% /,"")} testid="m-repaid" />
          <Card label="Avg advance"           value={fmtMoney(inv.average_advance_amount)} sub={`${data.avg_repay_days ?? "—"} day avg repay`} testid="m-avg" />
          <Card label="% gas or vehicle"      value={pct(inv.pct_gas_or_vehicle)} sub="combined use case" testid="m-gas" />
          <Card label="% payout in 6 days"    value={pct(inv.pct_payout_within_6_days)} sub="match for our window" testid="m-payout" />
          <Card label="Interview opt-in"      value={pct(inv.interview_optin_rate)} sub="ready to talk" testid="m-int" />
          <Card label="Testimonial opt-in"    value={pct(inv.testimonial_optin_rate)} sub="willing to share story" testid="m-test" />
        </div>
        {inv.waitlist_total > 0 && (
          <div className="mt-4 rounded-xl bg-[#00FF88]/8 border border-[#00FF88]/25 px-4 py-3 text-sm text-white/75">
            <strong className="text-white">Waitlist → Pilot conversion:</strong>{" "}
            <span className="text-[#00FF88] font-bold">{pct(inv.waitlist_to_pilot_conversion_pct)}</span>{" "}
            <span className="text-white/55 text-xs">({Math.round((inv.waitlist_to_pilot_conversion_pct/100) * inv.waitlist_total)} of {inv.waitlist_total.toLocaleString()} waitlist members applied)</span>
          </div>
        )}
        {inv.repeat_applicants > 0 && (
          <div className="mt-2 text-xs text-white/45 font-mono-c">Repeat applicants: {inv.repeat_applicants}</div>
        )}
      </Section>

      {/* Validation */}
      <Section icon={Target} eyebrow="// Validation metrics" title="What the data is telling us">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="% need gas"       value={pct(v.gas_pct)} />
          <Card label="% vehicle expense" value={pct(v.vehicle_pct)} />
          <Card label="% bills"          value={pct(v.bills_pct)} />
          <Card label="% childcare"      value={pct(v.childcare_pct)} />
          <Card label="% multi-platform" value={pct(v.multi_platform_pct)} />
          <Card label="% interview yes"  value={pct(v.interview_pct)} />
          <Card label="% testimonial"    value={pct(v.testimonial_pct)} />
          <Card label="% beta tester"    value={pct(v.beta_pct)} />
          <Card label="% approved"       value={pct(v.approved_pct)} />
          <Card label="% funded"         value={pct(v.funded_pct)} />
          <Card label="% repaid on time" value={pct(v.repaid_within_6_pct)} />
          <Card label="% payout ≤ 6d"    value={pct(v.next_payout_within_6_pct)} />
        </div>
      </Section>

      {/* Demand */}
      <Section icon={BarChart3} eyebrow="// Demand breakdown" title="Where demand is coming from">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          {[
            ["by_state",            "By state"],
            ["by_city",             "By city"],
            ["by_primary_platform", "By primary platform"],
            ["by_platform",         "By platform (multi)"],
            ["by_amount",           "By amount requested"],
            ["by_use_case",         "By use case"],
            ["by_weekly_earnings",  "By weekly earnings"],
            ["by_next_payout",      "By next payout"],
            ["by_acquisition",      "By acquisition source"],
            ["by_zip",              "By ZIP code"],
          ].map(([k, label]) => (
            <div key={k}>
              <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40 mb-2">{label}</div>
              <div className="space-y-1.5">
                {(d[k] || []).slice(0, 8).map((row) => (
                  <Bar key={row.key} label={row.key} count={row.count} pctv={row.pct} />
                ))}
                {!(d[k] || []).length && <div className="text-[11px] text-white/30">No data yet</div>}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default PilotMetrics;
